import React, { useState, useEffect, useRef } from 'react';
import { Dialog, IconButton, Button, Icon, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  RootWrap,
  TD_FORM_STYLE,
  TD_TITLE_STYLE,
  TD_SCREEN_WIDTH,
  TD_SCREEN_CONTAINER,
  TD_SCREEN_CONTAINER_MOBILE,
  StyledAppBar,
  StyledToolbar,
  Title,
  Status,
  StatusIcon,
  Container,
  BarTitle,
  ButtonContainer,
  DeleteButton,
  RowOutline,
  TagContainer,
  CreatedText,
  HideSmall,
  NextListItem,
  TagButton,
} from './TaskDetail.styles';
import { useSelector, useDispatch } from 'react-redux';
import setFilter from 'redux/actions/setFilter';
import { TaskFilter } from 'global/utils/filter';
import { get, size, first, set, unset, last, sortBy, noop, snakeCase } from 'global/lib/lodash';
import Form from 'global/components/form';
import { useScreen, Loading } from 'global/styles';
import Alert from '@mui/material/Alert';
import useRetrieveData from 'global/hooks/useRetrieveData';
import useAccessRedirect from 'global/hooks/useAccessRedirect';
import { setBodyOverflow } from 'global/hooks/useBodyOverflow';
import { getTaskStatus, taskNumToString, uppercaseFirst, getUrlParams } from 'global/utils';
import sendData from 'redux/actions/sendData';
import { RenderTag } from '../TaskCreate/TaskCreate';
import Confirm from 'global/components/confirm';
import { withRouter } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Styles, { Loader } from 'global/styles';
import TaskTag from '../TaskCreate/TaskTag';
import { UploadFiles, uploadImageToPerform } from 'redux/actions/imageUploader';
import { useUser } from 'global/hooks/useUser';
import { Text } from 'global/components/text';
import { CommentContainer, allowedToEdit } from './comments';
import moment from 'moment';
import { useSocket } from '../../../global/hooks/useSocket';
import { fixWeirdDateIssue } from '../../../global/components/form';
import { taskOptions } from '../../admin/account-settings';
import { TaskScoreRef } from '../CreateModal';
import { useHashView } from '../../share/useHashView';
import HashAccessBanner from '../../share/HashAccessBanner';
import ShareLinkModal from '../../share/ShareLinkModal';
import SnackAlerts from 'global/components/snack-alerts';
import { useTaskTypes } from '../task-type-utils/useTaskTypes';
import { getTaskTypeById, getCustomFieldInputs, getCloseoutFromTaskType, getCloseoutDefaults, getStandardFieldRequirements, cleanCustomFieldMedia, getCustomFieldMediaIds, patchCustomFieldMediaCaptions, UploadCustomFieldMedia } from '../task-type-utils';
import { splitMediaByType } from 'global/utils/mediaUtils';

export const TaskParams = {
  build: ['tasks'],
  transformData: (d) => {
    const v = first(d);

    const newmeta = {};

    for (let k in v.meta) {
      const val = v.meta[k];
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        const nested = {};
        for (let nk in val) {
          nested[snakeCase(nk)] = val[nk];
        }
        set(newmeta, snakeCase(k), nested);
      } else {
        set(newmeta, snakeCase(k), val);
      }
    }

    v.meta = newmeta;

    return v;
  },
  params: {
    include: [
      'images',
      'files',
      'assignees',
      'viewers',
      'authors',
      'comments',
      'comments.author',
      'comments.images',
      'comments.files',
      'realm',
      'scorecardScore',
      'scorecardScore.checklist',
      'scorecardScore.checkpoint',
      'reportSection',
      'reportSection.images',
      'subScore',
      'subScore.scorecardScore',
      'tradeTypes',
    ].join(','),
  },
};

export const DetailModal = (props) => {
  const { open } = props;
  return !open ? null : <TaskDetail {...props} />;
};

const TaskDetail = withRouter((props) => {
  const [canEdit, setCanEdit] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState();
  const [formState, setFormState] = useState({});
  let [comments, setComments] = useState([]);
  const [realmId, setRealmId] = useState(null);
  // PFM-2547: hashed share-link viewers can't navigate away; suppress the close button.
  const { active: hashViewActive } = useHashView();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // PFM-2648: confirmation toast after a Share email is sent (modal closes on send).
  const [shareSentAlert, setShareSentAlert] = useState();

  const [deleteConfirm, setDeleteConfirm] = useState();
  const [tag, setTag] = useState();
  const [tagName, setTagName] = useState();
  const [tagModal, setTagModal] = useState(false);
  const [customFieldErrors, setCustomFieldErrors] = useState([]);

  const [id, setId] = useState(props.taskId || get(props, 'match.params.id'));
  const formRef = useRef();
  // PFM-2686: track which task ids we've already applied list-filter defaults for,
  // so opening a task via deep-link sets the filter once without clobbering later
  // user changes or refiring on in-list next/prev navigation.
  const appliedFilterForId = useRef();

  const styles = Styles();
  const dispatch = useDispatch();
  const uhook = useUser();
  const { me } = uhook;
  const taskList = useSelector((state) => get(state, 'view.task.list'));
  const realmFilter = useSelector((state) => get(state, 'filter.tasks.realm', TaskFilter({ ownership: 'all' })));
  const { isMobileXS } = useScreen();

  const loca = useLocation();
  const { tcid } = getUrlParams(get(loca, 'search'));

  const taskData = useRetrieveData({ url: `/tasks/${id}` }, TaskParams);
  const { data, loading, update } = taskData;

  // PFM-2565: when reached via the /tasks/detail/:id deep-link route (no taskId prop)
  // and the user can't access this task, redirect to /tasks/list with an alert.
  useAccessRedirect(taskData, { skip: !!props.taskId || !get(props, 'match.params.id') });
  const users = useRetrieveData(
    { url: '/users' },
    {
      waitFor: realmId,
      build: ['users'],
      params: { filter: { content_realm_id: realmId }, useCache: { reload: 1 } },
    }
  );
  const tci = useRetrieveData(
    { url: `/processed-task-count-values` },
    {
      waitFor: !tcid && realmId,
      build: ['processedTaskCountValues'],
      params: { filter: { realmid: realmId } },
      transformData: (da) => get(da, '0', {}),
    }
  );
  const d = data;
  const authuser = get(d, 'authors.0', {});
  const status = getTaskStatus(d);
  const { data: taskTypesData } = useTaskTypes();
  const taskType = getTaskTypeById(taskTypesData, get(d, 'taskTypeId', get(d, 'task_type_id', get(d, 'meta.task_type_id'))));
  const useHistory = get(props, 'useHistory', true);

  const scorecardId = get(d, 'scorecardScore.scorecardId') || get(d, 'subScore.scorecardScore.scorecardId');
  const scData = useRetrieveData(
    { url: `/scorecards/${scorecardId}` },
    {
      waitFor: !scorecardId ? null : undefined,
      build: ['scorecards'],
    }
  );
  const scorecard = get(scData, 'data.0');
  const scorecardConfig = get(scData, 'data.0.config');

  useEffect(() => {
    if (d) {
      setComments(get(d, 'comments', []));
      setRealmId(get(d, 'realm.id', null));

      if (get(d, 'scorecardScore')) {
        const scs = get(d, 'scorecardScore');
        const cp = get(scs, 'checkpoint');

        const _tag = {
          scorecardConfig: scorecardConfig,
          ...cp,
          ...scs,
          pssvCheckpoint: cp,
          pssvChecklist: get(scs, 'checklist'),
          scorecardScoreId: get(d, 'scorecardScoreId')
        };
        setTag(_tag);
      } else if (get(d, 'subScore')) {
        const { subScore, ...rest } = d;
        setTag({
          ...rest,
          scorecardConfig: scorecardConfig,
          ...get(subScore, 'scorecardScore', {}),
          ...subScore,
          pssvCheckpoint: subScore,
          pssvChecklist: get(subScore, 'checklist'),
          scorecardScoreId: get(d, 'subScore.scorecardScoreId')
        });
      } else if (get(d, 'reportSection')) {
        setTag({
          ...d,
        });
      } else {
        setTag();
      }
    }
  }, [d, scorecard]);

  // PFM-2686: when the user lands STRAIGHT on a task (rather than opening it from
  // the list mid-session), align the Tasks list filters to the task so the underlying
  // list still shows it after the detail closes. Two routes count as "landed straight on":
  //   1. the standalone deep-link route /tasks/detail/:id (no taskId/onClose props), and
  //   2. a page refresh/deep-link while a task modal was open — TaskRealm re-opens it from
  //      the `?detail=` URL param and passes restoredFromUrl (the embedded refresh case the
  //      filter slice is NOT persisted, so it has reset to the "All Tasks" default by now).
  // An in-session click sets neither flag, so its filters are left untouched (matches the
  // "open & close in the same session keeps your previous filters" rule). Ownership defaults
  // to "My Tasks" unless the user is neither author nor assignee (then "All Tasks"); status
  // defaults to "Open / Pending" unless the task is Closed (then "Closed"). Runs once per
  // task id so it never clobbers a filter the user changes afterward.
  const isStandalone = !props.taskId && !!get(props, 'match.params.id');
  const alignListToTask = isStandalone || !!props.restoredFromUrl;
  useEffect(() => {
    // Wait until useRetrieveData has fully finished (loading === false). `d` becomes
    // truthy with the task's own attributes BEFORE relationships (authors/assignees)
    // are normalized; gating on `loading` ensures they're hydrated before we read
    // ownership, and only then do we lock the one-shot ref. Skip for anonymous
    // shared-task viewers (no current user) since there's no list to return to.
    if (!alignListToTask || loading || !d || !me || appliedFilterForId.current === id) {
      return;
    }
    appliedFilterForId.current = id;

    const isAuthor = get(d, 'authors', []).some((a) => a.id === me.id);
    const isAssignee = get(d, 'assignees', []).some((a) => a.id === me.id);
    const ownership = isAuthor || isAssignee ? 'my' : 'all';
    // Normalized task `status` is numeric (0/1/2); coerce to its string form so the
    // Closed check is robust to either a number or an already-string value.
    const isClosed = taskNumToString(get(d, 'status')) === 'closed' || get(d, 'status') === 'closed';
    const taskStatus = isClosed ? 'closed' : 'openPending';

    // setFilter with filterType 'tasks' runs matchInFilter, syncing these keys
    // across the list/realm/scorecard slots — intended here.
    dispatch(setFilter('tasks', 'realm', { ...realmFilter, ownership, status: taskStatus }));
    // realmFilter is intentionally omitted: this is a one-shot keyed by `id`, so we
    // read the latest filter at apply-time without re-running when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, id, loading, alignListToTask, me?.id, dispatch]);

  useSocket(
    {
      channel: `task.${id}`,
      onEvent: (resp) => {
        const comment = get(resp, 'data', get(resp, 'comment', resp));

        setComments([...comments, comment]);
      },
    },
    [comments]
  );

  const toggleEdit = () => {
    setCanEdit(!canEdit);
  };

  const ate = allowedToEdit(d, comments, { user: uhook, hashViewActive });

  const formatData = async (values) => {
    const relationships = {};
    const assignedTo = get(values, 'assignedTo');

    let tresult;
    if (values.tagAdded && tag) {
      const selectedOption = get(tag, 'pssvCheckpoint.selectedOption');
      const sco = get(tag, `pssvCheckpoint.scorecardOptions.${get(selectedOption, 'index')}`);
      tresult = await sendData({
        url: '/scorecard-scores',
        method: 'post',
        data: {
          data: {
            type: 'scorecard-scores',
            attributes: {
              scorecard_id: get(tag, 'scorecardId'),
              checklist_id: get(tag, 'checklistId'),
              checkpoint_id: get(tag, 'checkpointId'),
              scorecard_option_id: get(sco, 'id'),
              criteria: get(tag, 'criteria'),
              score: get(sco, 'value', 0),
            },
          },
        },
      });
    }

    const getStatus = () => {
      const createComment = last(comments);
      if (createComment) {
        const newStat = get(createComment, 'newStatus');
        if (newStat) {
          return newStat === 'pending' ? 1 : newStat === 'closed' ? 2 : 0;
        } else {
          return status.status;
        }
      }
    };

    const stat = getStatus();

    // Clean custom field media values: patch captions for changed items, then strip to just {type, id}
    let cleanedCfv;
    if (taskType) {
      const rawCfv = get(values, 'custom_field_values', get(formState, 'custom_field_values', {}));
      // Upload any custom field media files that still have temporary _file_ IDs
      for (const key in rawCfv) {
        const val = rawCfv[key];
        if (Array.isArray(val)) {
          for (let i = 0; i < val.length; i++) {
            const item = val[i];
            if (item && item.file && `${item.id}`.includes('_file_')) {
              const res = await uploadImageToPerform({
                ...item,
                collection: item.collection_name || item.collectionName || item.collection || 'task-images',
                custom_properties: {
                  caption: item.caption || get(item, 'custom_properties.caption', ''),
                  generated_conversions: { large: true, thumb: true },
                },
              });
              val[i] = { ...res, ...get(res, 'attributes', {}), type: 'media' };
            }
          }
        }
      }
      await patchCustomFieldMediaCaptions(rawCfv);
      cleanedCfv = cleanCustomFieldMedia(rawCfv);
    }

    // Filter custom field media out of regular images so they aren't double-counted
    const originalCfv = get(d, 'meta.custom_field_values', get(d, 'customFieldValues', {}));
    const allCfMediaIds = new Set([
      ...getCustomFieldMediaIds(originalCfv),
      ...getCustomFieldMediaIds(cleanedCfv || {}),
    ]);

    // Split combined media field back into images and files for API
    const combinedMedia = get(values, 'media', [...get(d, 'images', []), ...get(d, 'files', [])]);
    const { images: splitImages, documents: splitFiles } = splitMediaByType(
      combinedMedia.filter(item => !allCfMediaIds.has(String(item.id)))
    );

    const attributes = {
      name: get(values, 'name'),
      description: get(values, 'description'),
      status: stat,
      images: splitImages,
      files: splitFiles,
      due_date: get(values, 'dueDate'),
      meta: (() => {
        const m = {
          ...(taskType ? getCloseoutDefaults(taskType) : {}),
          ...get(d, 'meta', {}),
          ...get(values, 'meta'),
        };
        // Remove hydrated custom field values; cleaned version is set by UploadCustomFieldMedia
        unset(m, 'customFieldValues');
        unset(m, 'custom_field_values');
        delete m.task_type_id;
        return m;
      })(),
      ...(taskType ? { task_type_id: String(taskType.id) } : {}),
    };

    if (values.tagAdded) {
      set(attributes, 'scorecard_score_id', get(tresult, 'data.data.id', null));
    } else if (values.tagRemoved) {
      // REMOVE LINK: clearTag() only updates local state, so the PATCH must
      // explicitly null out the link to actually unlink it. A task is linked
      // EITHER via scorecard_score_id (library link) OR sub_score_id
      // (assessment link), so null both to cover both cases.
      set(attributes, 'scorecard_score_id', null);
      set(attributes, 'sub_score_id', null);
    }

    if (assignedTo !== undefined) {
      set(relationships, 'assignees', {
        data: size(assignedTo) ? assignedTo.map((e) => ({ type: 'users', role: 'assignee', id: e.id })) : [{ type: 'users', role: 'assignee', id: me.id }],
      });
    }

    const comms = comments.filter((comment) => comment.isNew);
    if (size(comms)) {
      await Promise.all(
        comms.map(async (comment) => {
          // Add signature file to images if present
          const sigData = get(comment, 'signature.data');
          if (sigData) {
            const blob = await (await fetch(sigData)).blob();
            const sigFile = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });
            sigFile.path = sigFile.name;
            const imgs = get(comment, 'images', []);
            imgs.push({ file: sigFile, name: sigFile.name, collection: 'signature-images' });
            comment.images = imgs;
          }

          const _data = {
            data: {
              type: 'comments',
              attributes: {
                user_id: get(me, 'id'),
                ...comment,
                commentable_id: id,
                commentable_type: 'task',
                body: get(comment, 'body', ''),
                meta: {
                  showCorrection: stat !== status.status,
                  status: stat,
                },
              },
            },
          };

          await UploadFiles(_data, { attributeType: 'images' });

          if (size(get(comment, 'files', []))) {
            await UploadFiles(_data, { attributeType: 'files', isImage: false, collection_name: 'comment-files' });
          }

          return await sendData({
            url: `/comments`,
            method: 'post',
            data: _data,
          });
        })
      );
    }

    const __data = {
      data: {
        id,
        type: 'tasks',
        attributes,
        relationships,
      },
    };

    const tradeTypes = get(values, 'tradeTypes');
    if (size(tradeTypes)) {
      set(__data, 'data.relationships.tradeTypes', {
        data: (tradeTypes || []).map((t, i) => ({ type: 'trade-types', id: t.id, pivot: { order: `${i}` } })),
      });
    }

    const shouldUpdate = !!get(attributes, 'images', []).find((im) => im.hasChanged || im.id.indexOf('_file_') > -1);
    await UploadFiles(__data, { shouldUpdate });

    const docFiles = get(attributes, 'files', []);
    if (size(docFiles)) {
      const shouldUpdateFiles = !!docFiles.find((f) => f.hasChanged || (f.id || '').toString().indexOf('_file_') > -1);
      await UploadFiles(__data, { attributeType: 'files', isImage: false, collection_name: 'task-files', shouldUpdate: shouldUpdateFiles });
    } else {
      // Clear attributes.files so it doesn't override relationships.files set by UploadCustomFieldMedia
      set(__data, 'data.attributes.files', undefined);
    }

    // Process custom field media: set cleaned values and add to relationships
    // Must run AFTER UploadFiles for both images and files, since UploadFiles replaces
    // relationships and this merges custom field media IDs on top.
    if (taskType) {
      const rawCfvForRels = get(values, 'custom_field_values', get(formState, 'custom_field_values', {}));
      await UploadCustomFieldMedia(__data, {
        rawCfv: rawCfvForRels,
        taskTypeId: taskType.id,
        skipCaptions: true,
      });
    }

    return __data;
  };

  const formSubmit = async (values, notify_users) => {
    try {
      setFormLoading(true);
      const _data = await formatData(values);
      if (notify_users) {
        set(_data, 'data.attributes.notify_users', notify_users);
      }
      await sendData(
        {
          url: `/tasks/${id}`,
          method: 'patch',
          data: _data,
        },
        { simulate: false }
      );
      await update();
      setFormLoading(false);
      setCanEdit(false);
    } catch (err) {
      setFormLoading(false);
      setError(`${err.message}. Please try again.`);
    }
  };

  const formUpdate = (vals) => {
    for (let k in vals) {
      set(formState, k, vals[k]);
    }
    setFormState({ ...formState });
  };

  const handleClose = () => {
    if (props.onClose) {
      // When opened as a modal (e.g. from assessment view), let MUI Dialog
      // handle body overflow restoration — don't interfere with its ModalManager.
      return props.onClose(first(data));
    }
    // Standalone mode: clear body overflow before navigating away
    setBodyOverflow();
    if (!tcid && props.history.length > 2) {
      props.history.goBack();
      const location = get(props, 'history.location.pathname');
      setTimeout(() => {
        if (get(props, 'history.location.pathname') === location) {
          props.history.goBack();
        }
      }, 200);
    } else {
      const tciid = tcid ? tcid : get(tci, 'data.id');
      if (tciid) {
        const params = realmId ? `?realmId=${realmId}` : '';
        props.history.push(`/tasks/list/${tciid}${params}`);
      } else {
        props.history.goBack();
      }
    }
  };

  const onSubmitComment = async (comms, notify_users, assigneeOverride) => {
    try {
      comments = comms;
      setComments(comms);
      const vals = { ...formState };
      if (assigneeOverride) {
        vals.assignedTo = assigneeOverride;
      }
      await formSubmit(vals, notify_users);
      update();
    } catch (err) {
      setError(err.message);
    }
  };

  const confirmDelete = async () => {
    try {
      setFormLoading(true);
      await sendData({
        url: `/tasks/${id}`,
        method: 'patch',
        data: {
          data: {
            id,
            type: 'tasks',
            attributes: {
              archived: true,
            },
          },
        },
      });
      setFormLoading(false);
      handleClose();
    } catch (err) {
      setError(`Error: ${err.message}`);
      setFormLoading(false);
    }
  };

  const onDelete = () => {
    setDeleteConfirm({
      title: 'Delete Task',
      content: 'Are you sure you want to delete this task?',

      onCancel: () => setDeleteConfirm(),
      onOk: () => {
        confirmDelete();
        setDeleteConfirm();
      },
    });
  };

  useEffect(() => {
    if (!useHistory) {
      setDetailParam();
    }
  }, [id]);

  const setDetailParam = (_id = id) => {
    const search = new URLSearchParams(loca.search);
    search.set('detail', _id);
    props.history.replace(`${loca.pathname}?${search.toString()}`);
  };

  const nextListItem = (dir = 'right') => {
    const i = taskList.findIndex((t) => t.id === id);
    const nextItem = (() => {
      if (dir === 'right') {
        return taskList[i + 1] ? taskList[i + 1] : first(taskList);
      } else {
        return taskList[i - 1] ? taskList[i - 1] : last(taskList);
      }
    })();
    if (useHistory) {
      props.history.replace(`/tasks/detail/${get(nextItem, 'id')}${get(loca, 'search', '')}`);
    }
    setId(get(nextItem, 'id'));
  };

  const clearTag = () => {
    if (get(tag, 'assessmentId', null) !== null) {
      setDeleteConfirm({
        title: 'Remove Link?',
        content: 'This task is linked to an assessment, are you sure you want to remove the link? This task will no longer be linked to the assessment afterwards.',

        onCancel: () => setDeleteConfirm(),
        onOk: () => {
          setTag();
          setTagName();
          formRef.current.updateState('tagAdded', false);
          formRef.current.updateState('tagRemoved', true);
          if (tagName === get(formState, 'name')) {
            formRef.current.updateState('name', '');
          }
          setDeleteConfirm();
        },
      });
    } else {
      setTag();
      setTagName();
      formRef.current.updateState('tagAdded', false);
      formRef.current.updateState('tagRemoved', true);
      if (tagName === get(formState, 'name')) {
        formRef.current.updateState('name', '');
      }
      setDeleteConfirm();
    }
  };

  const onTagSelected = (tags) => {
    setTag(tags);
    formRef.current.updateState('tagAdded', true);
    if (!get(formState, 'name')) {
      const clname = get(tags, 'pssvChecklist.name');
      const cpname = get(tags, 'pssvCheckpoint.name');
      const name = clname + (cpname ? `: ${cpname}` : '');
      setTagName(name);
      formRef.current.updateState('name', name);
    }
  };

  return (
    <Dialog fullScreen disableEscapeKeyDown open={true} onClose={handleClose}>
     <RootWrap canEdit={canEdit}>
      {hashViewActive && <HashAccessBanner />}
      <StyledAppBar color="primary">
        <StyledToolbar>
          {!hashViewActive && (
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="Close" size="large">
              <CloseIcon />
            </IconButton>
          )}

          <BarTitle>
            <Title variant="h5">
              {taskType ? `${taskType.name}: ${get(d, 'name', Loading)}` : get(d, 'name', Loading)}
            </Title>
          </BarTitle>

          <Status variant="h6">
            <HideSmall>{uppercaseFirst(status.title)}</HideSmall>
            <StatusIcon><Icon>{status.icon}</Icon></StatusIcon>
          </Status>
        </StyledToolbar>
      </StyledAppBar>

      <Container>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {loading && (
          <div className={styles.centerColumn}>
            <Loader size={20} />
          </div>
        )}

        {!formLoading && !loading && (
          <>
            <ButtonContainer sx={{ justifyContent: canEdit ? 'flex-start' : 'space-between' }}>
              {ate.show && (
                <Button color={canEdit ? 'notice' : 'secondary'} variant="contained" disabled={ate.disabled} onClick={toggleEdit} startIcon={<Icon>{canEdit ? 'close' : 'edit'}</Icon>}>
                  {'Edit'}
                </Button>
              )}
              {!hashViewActive && id && !canEdit && (
                <Button
                  data-testid="task-share-btn"
                  color={'secondary'}
                  variant="contained"
                  onClick={() => setShareModalOpen(true)}
                  startIcon={<Icon>ios_share</Icon>}
                >
                  {'Share'}
                </Button>
              )}
              {canEdit && (
                <Tooltip title={ate.delete ? '' : 'Only the author or system admins can delete a task'}>
                  <span>
                    <DeleteButton color="error" disabled={!ate.delete} onClick={onDelete} variant="contained" startIcon={<Icon>delete</Icon>}>
                      {'Delete'}
                    </DeleteButton>
                  </span>
                </Tooltip>
              )}
              <Confirm {...deleteConfirm} />
            </ButtonContainer>
          </>
        )}

        {!loading && !formLoading && d && (
          <RowOutline>
            <Text type="secondarySub">Created</Text>
            <TagContainer>
              <Text>{get(authuser, 'name', 'Unknown')}</Text>
              <CreatedText as={Text}>{`Created: ${moment(get(d, 'createdAt')).format('L [at] h:mma')}`}</CreatedText>
            </TagContainer>
          </RowOutline>
        )}

        {!loading && (
          <div>
            <Form
              loading={formLoading || loading}
              showAllScreens={true}
              formStyle={TD_FORM_STYLE}
              onSubmit={formSubmit}
              onFormState={formUpdate}
              canSubmit={canEdit}
              ref={formRef}
              submitText="Save Changes"
              screens={[
                {
                  title: '',
                  titleStyle: TD_TITLE_STYLE,
                  screenContainerStyle: isMobileXS ? TD_SCREEN_CONTAINER_MOBILE : TD_SCREEN_CONTAINER,
                  screenContainer: {
                    props: {
                      className: TD_SCREEN_WIDTH,
                    },
                  },
                  inputs: (() => {
                    // Determine which standard fields are required from task type
                    const stdReqs = taskType ? getStandardFieldRequirements(taskType) : [];
                    const isFieldRequired = (fid) => stdReqs.find((f) => f.id === fid)?.is_required;
                    const isFieldEnabled = (fid) => { const f = stdReqs.find((s) => s.id === fid); return f ? f.is_enabled !== false : true; };

                    // Default field input definitions keyed by their field_order IDs
                    const defaultFieldMap = {
                      f_title: {
                        id: 'name',
                        type: 'TextField',
                        required: isFieldRequired('f_title'),
                        defaultValue: () => get(d, 'name', ''),
                        props: { label: 'Title', disabled: !canEdit },
                      },
                      f_description: {
                        id: 'description',
                        type: 'TextField',
                        required: isFieldRequired('f_description'),
                        defaultValue: () => get(d, 'description'),
                        props: {
                          label: 'Description', disabled: !canEdit, multiline: true,
                          maxRows: 5, placeholder: 'N/A',
                          InputLabelProps: { shrink: true },
                        },
                      },
                      f_assignee: {
                        id: 'assignedTo',
                        type: 'UserSelect',
                        required: isFieldRequired('f_assignee'),
                        defaultValue: () => get(d, 'assignees', []),
                        data: () => get(users, 'data', []),
                        props: { multiple: true, label: 'Assigned To', disabled: !canEdit },
                      },
                      f_due_date: {
                        id: 'dueDate',
                        type: 'KeyboardDatePicker',
                        required: isFieldRequired('f_due_date'),
                        defaultValue: () => (get(d, 'dueDate') ? moment(get(d, 'dueDate')) : undefined),
                        props: { label: 'Due Date', disabled: !canEdit, convert: (dt) => fixWeirdDateIssue(dt, !formState.dueDate) },
                      },
                      f_trade: {
                        id: 'tradeTypes',
                        type: 'TradeSelect',
                        required: isFieldRequired('f_trade'),
                        defaultValue: () => get(d, 'tradeTypes', []),
                        props: { label: 'Trade Types', checklist: get(tag, 'pssvChecklist'), disabled: !canEdit },
                      },
                      f_media: (!canEdit && !size(get(d, 'images', [])) && !size(get(d, 'files', []))) ? null : {
                        id: 'media',
                        type: 'DropArea',
                        required: isFieldRequired('f_media'),
                        defaultValue: () => {
                          const _cfv = get(d, 'customFieldValues', get(d, 'custom_field_values', get(d, 'meta.custom_field_values', {})));
                          const _cfIds = new Set(getCustomFieldMediaIds(_cfv));
                          const images = sortBy(get(d, 'images', []), 'orderColumn')
                            .filter((img) => !_cfIds.has(String(img.id)))
                            .map((img) => ({
                              ...img,
                              canDelete: img.collectionName.includes('task-'),
                              tabProps: {
                                edit: {
                                  disabled: !img.collectionName.includes('task-'),
                                  toolTip: !img.collectionName.includes('task-') ? "This media is a reference and can't be edited." : undefined,
                                },
                              },
                            }));
                          const files = get(d, 'files', [])
                            .filter((file) => !_cfIds.has(String(file.id)))
                            .map((file) => ({
                              ...file,
                              canDelete: canEdit,
                            }));
                          return [...images, ...files];
                        },
                        props: {
                          label: 'Media', sortable: true, shouldUpdateOnCaptionChange: true,
                          assessment: props.assessment, uploadAllNew: true,
                          collection: 'task-images', parentId: id,
                          authors: get(data, '0.authors'), notAllowed: ['tag'],
                          disabled: !canEdit, spaceBetween: 5,
                          bothMode: true, accept: null,
                        },
                      },
                    };

                    const referenceInput = {
                      id: 'reference',
                      props: { label: 'Reference' },
                      component: () => {
                        return (
                          <div>
                            <TaskScoreRef {...props} checkpoint={tag} showLink={!canEdit} />
                            {canEdit && !get(tag, 'reportSection') && (
                              <TagButton
                                isRemove={!!tag}
                                variant="outlined"
                                startIcon={<Icon>{tag ? 'close' : 'label'}</Icon>}
                                onClick={tag ? () => clearTag() : () => setTagModal(true)}
                              >
                                {tag ? 'REMOVE LINK' : 'ADD LINK'}
                              </TagButton>
                            )}
                          </div>
                        )
                      }
                    };

                    const metaInput = canEdit ? {
                      id: 'meta',
                      type: 'Checkbox',
                      options: taskType ? getCloseoutFromTaskType(taskType) : taskOptions(),
                      defaultValue: () => {
                        if (taskType) {
                          const defaults = getCloseoutDefaults(taskType);
                          const meta = get(d, 'meta', {});
                          // Task's saved closeout values override task type defaults
                          Object.keys(defaults).forEach(k => {
                            if (meta[k] !== undefined) defaults[k] = meta[k];
                          });
                          return defaults;
                        }
                        return get(d, 'meta', {});
                      },
                      props: { label: 'Requirements', disabled: !ate.isAuthOrAdmin },
                    } : null;

                    const customFieldValues = get(d, 'customFieldValues', get(d, 'custom_field_values', get(d, 'meta.custom_field_values', {})));
                    const fieldOrder = get(taskType, 'field_order');

                    const taskImages = [...get(d, 'images', []), ...get(d, 'files', [])];

                    if (fieldOrder && Array.isArray(fieldOrder)) {
                      const customInputMap = {};
                      if (taskType) {
                        getCustomFieldInputs(taskType, customFieldValues, { disabled: !canEdit, errors: customFieldErrors, taskImages }).forEach((input) => {
                          const cfId = input.id.replace('custom_field_values.', '');
                          customInputMap[cfId] = input;
                        });
                      }

                      const ordered = [];
                      fieldOrder.forEach((fid) => {
                        if (fid === 'f_reference') {
                          ordered.push(referenceInput);
                        } else if (defaultFieldMap[fid] && isFieldEnabled(fid)) {
                          ordered.push(defaultFieldMap[fid]);
                        } else if (fid === 'f_documents') {
                          // f_documents is now merged into f_media, skip it
                        } else if (customInputMap[fid]) {
                          ordered.push(customInputMap[fid]);
                        }
                      });

                      // Append any custom fields not in field_order (newly added)
                      Object.keys(customInputMap).forEach((cfId) => {
                        if (!fieldOrder.includes(cfId)) {
                          ordered.push(customInputMap[cfId]);
                        }
                      });

                      if (!fieldOrder.includes('f_reference')) ordered.push(referenceInput);
                      if (metaInput) ordered.push(metaInput);
                      return ordered.filter((f) => f);
                    }

                    // Fallback: no field_order, use original hardcoded order
                    return [
                      defaultFieldMap.f_title,
                      ...(isFieldEnabled('f_description') ? [defaultFieldMap.f_description] : []),
                      ...(isFieldEnabled('f_assignee') ? [defaultFieldMap.f_assignee] : []),
                      ...(isFieldEnabled('f_due_date') ? [defaultFieldMap.f_due_date] : []),
                      ...(isFieldEnabled('f_trade') ? [defaultFieldMap.f_trade] : []),
                      ...(isFieldEnabled('f_media') ? [defaultFieldMap.f_media] : []),
                      referenceInput,
                      ...(taskType ? getCustomFieldInputs(taskType, customFieldValues, { disabled: !canEdit, errors: customFieldErrors, taskImages }) : []),
                      metaInput,
                    ].filter((f) => f);
                  })(),
                },
              ]}
            />
          </div>
        )}


        {!loading && !formLoading && d && !canEdit && <CommentContainer comments={comments} onSubmitComment={onSubmitComment} formState={formState} data={taskData} task={d} ate={ate} taskType={taskType} onCustomFieldErrors={setCustomFieldErrors} users={users} />}

        {size(taskList) > 1 && (
          <>
            <NextListItem left>
              <IconButton onClick={() => nextListItem('left')} size="large">
                <Icon style={{ transform: 'rotate(90deg)' }}>expand_circle_down</Icon>
              </IconButton>
            </NextListItem>
            <NextListItem>
              <IconButton onClick={() => nextListItem()} size="large">
                <Icon style={{ transform: 'rotate(270deg)' }}>expand_circle_down</Icon>
              </IconButton>
            </NextListItem>
          </>
        )}
      </Container>
      <TaskTag open={tagModal} onTagSelected={onTagSelected} onClose={() => setTagModal(false)} />
      {shareModalOpen && (
        <ShareLinkModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          onSent={({ message }) => setShareSentAlert({ message })}
          scope={{ kind: 'task', id }}
          title={get(d, 'name', 'task')}
        />
      )}
      <SnackAlerts alerts={shareSentAlert} setAlerts={setShareSentAlert} />
     </RootWrap>
    </Dialog>
  );
});

export default TaskDetail;
